<template>
    <button
        v-if="!isVissible"
        @click="toggleSearch"
        type="button"
        class="relative size-8"
    >
        <div
            class="icon-search pointer-events-none absolute flex text-graphite-9 items-center text-xl top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        ></div>
    </button>
    <div v-if="isVissible" class="relative w-full">
        <label for="organic-search" class="sr-only">Search</label>

        <button
            type="button"
            @click="toggleSearch"
            class="absolute top-2 right-3 z-10 flex items-center text-graphite-4 text-xl transition-colors hover:text-graphite-9"
            aria-label="Close Search"
        >
            <span class="icon-cancel pointer-events-none"></span>
        </button>

        <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="block w-[28rem] rounded-2xl border border-borderGray font-normal py-2 pr-10 pl-4 text-graphite-9 text-14 leading-145 -tracking-0.5 placeholder:text-graphite-4"
            :minlength="minQueryLength"
            :maxlength="1000"
            :placeholder="placeholder"
            aria-required="true"
            @input="onSearchInput"
            @focus="onFocus"
            @blur="onBlur"
            @keydown.down.prevent="highlightNext"
            @keydown.up.prevent="highlightPrev"
            @keydown.enter.prevent="onEnter"
            @keydown.escape="closeDropdown"
        />

        <div
            v-if="isOpen && (hasResults || isLoading)"
            ref="dropdown"
            class="absolute left-0 top-full z-50 mt-3 w-full md:w-[42rem] max-h-[80vh] overflow-y-auto rounded-2xl border border-gray-100 bg-white shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)]"
        >
            <div v-if="isLoading" class="p-6 text-center text-graphite-5">
                <span class="icon-spinner icon-spin text-3xl"></span>
                <p class="mt-2 text-sm">{{ searchingText }}</p>
            </div>

            <div
                v-else-if="!hasResults && searchQuery.length >= minQueryLength"
                class="p-6 text-center text-graphite-5"
            >
                <p class="mt-2 text-sm">{{ noResultsText }}</p>
            </div>

            <div v-else-if="hasResults" class="flex flex-col p-2">
                <div
                    v-if="results.products?.total > 0"
                    class="overflow-hidden px-4 pb-4 pt-4"
                >
                    <h3 class="mb-4 text-[14px] font-semibold text-graphite-6">
                        {{ productsText }}
                    </h3>
                    <div class="space-y-0 overflow-hidden">
                        <a
                            v-for="item in results.products.hits.slice(0, 3)"
                            :key="'prod-' + item.id"
                            :href="getProductUrl(item)"
                            class="group flex items-stretch gap-4 border-b border-graphite-1 py-2 pl-0 pr-0 transition-all last:border-b-0 hover:bg-gray-50"
                            :class="{
                                'bg-gray-50':
                                    highlightedIndex ===
                                    getGlobalIndex('products', item),
                            }"
                            @mousedown.prevent
                        >
                            <div
                                class="relative h-[117px] w-[164px] flex-shrink-0 overflow-hidden bg-gray-100"
                            >
                                <img
                                    v-if="item.image_url"
                                    :src="item.image_url"
                                    :alt="item.name"
                                    class="h-[117px] w-[164px] object-cover transition-transform duration-300 group-hover:scale-105"
                                />
                                <div
                                    class="absolute left-2 top-2 flex items-center gap-1 bg-misty-pine-1 px-2 py-1 text-[11px] font-medium text-misty-pine-5"
                                >
                                    <component
                                        :is="
                                            getFormatIcon(
                                                item.type || item.format,
                                            )
                                        "
                                        class="w-4 h-4 shrink-0"
                                    />
                                    {{
                                        formatLabel(item.type || item.format) ||
                                        "Course"
                                    }}
                                </div>
                            </div>

                            <div
                                class="flex min-h-[117px] flex-1 flex-col justify-between py-0.5"
                            >
                                <div>
                                    <h4
                                        class="text-[16px] font-medium leading-tight !font-sans text-graphite-9 line-clamp-2"
                                    >
                                        {{ item.name }}
                                    </h4>
                                    <p class="mt-1 text-[14px] text-graphite-6">
                                        {{
                                            item.content_provider ||
                                            item.seller_name ||
                                            item.provider ||
                                            "—"
                                        }}
                                    </p>
                                </div>

                                <div
                                    v-if="item.recommended_level"
                                    class="flex flex-wrap gap-2"
                                >
                                    <span
                                        class="rounded-full bg-misty-pine-1 px-2.5 py-1 text-[11px] font-medium text-misty-pine-5"
                                    >
                                        {{ item.recommended_level }}
                                    </span>
                                </div>
                            </div>
                        </a>
                    </div>

                    <a
                        v-if="results.products.total > 3"
                        :href="getProductSearchUrl()"
                        class="mt-4 flex items-center gap-1.5 pl-0 pr-2 text-[13px] font-medium text-gray-600 transition-colors hover:text-gray-900"
                        @mousedown.prevent
                    >
                        View all {{ results.products.total }} Products
                        <svg
                            class="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M14 5l7 7m0 0l-7 7m7-7H3"
                            ></path>
                        </svg>
                    </a>
                </div>

                <div
                    v-if="
                        results.products?.total > 0 &&
                        (results.categories?.total > 0 ||
                            results.sellers?.total > 0)
                    "
                    class="-mx-2 border-t border-graphite-1"
                ></div>

                <div
                    v-if="results.categories?.total > 0"
                    class="px-4 pb-4 pt-4"
                >
                    <h3 class="mb-3 text-[14px] font-semibold text-graphite-6">
                        {{ categoriesText }}
                    </h3>
                    <div class="space-y-0.5">
                        <a
                            v-for="item in results.categories.hits.slice(0, 3)"
                            :key="'cat-' + item.id"
                            :href="item.url"
                            class="block rounded-lg py-2 pl-0 pr-2 text-[15px] text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900"
                            :class="{
                                'bg-gray-50 text-gray-900':
                                    highlightedIndex ===
                                    getGlobalIndex('categories', item),
                            }"
                            @mousedown.prevent
                        >
                            {{ item.name }}
                        </a>
                    </div>
                </div>

                <div
                    v-if="
                        results.categories?.total > 0 &&
                        results.sellers?.total > 0
                    "
                    class="-mx-2 border-t border-graphite-1"
                ></div>

                <div v-if="results.sellers?.total > 0" class="px-4 pb-4 pt-4">
                    <h3 class="mb-3 text-[14px] font-semibold text-graphite-6">
                        Content Providers
                    </h3>
                    <div class="flex flex-col space-y-1">
                        <a
                            v-for="item in results.sellers.hits.slice(0, 3)"
                            :key="'seller-' + item.id"
                            :href="getSellerUrl(item)"
                            class="flex items-center gap-3 rounded-lg py-2 pl-0 pr-2 transition-colors hover:bg-gray-50"
                            :class="{
                                'bg-gray-50':
                                    highlightedIndex ===
                                    getGlobalIndex('sellers', item),
                            }"
                            @mousedown.prevent
                        >
                            <div
                                class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-[#2A3F5A] text-white overflow-hidden shadow-sm"
                            >
                                <img
                                    v-if="item.logo_url"
                                    :src="item.logo_url"
                                    :alt="item.shop_title"
                                    class="h-full w-full object-cover"
                                />
                                <LogoIcon
                                    v-else
                                    class="h-4 w-4 shrink-0"
                                    style="filter: brightness(0) invert(1)"
                                />
                            </div>
                            <div class="flex-1 min-w-0">
                                <p class="text-[15px] text-gray-700">
                                    {{ item.shop_title }}
                                </p>
                            </div>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import CourseIcon from "@/js/svg/CourseIcon.vue";
import LogoIcon from "@/js/svg/LogoIcon.vue";
import OnSiteIcon from "@/js/svg/OnSiteIcon.vue";
import OnlineIcon from "@/js/svg/OnlineIcon.vue";
import VideoIcon from "@/js/svg/VideoIcon.vue";
import WebinarIcon from "@/js/svg/WebinarIcon.vue";

const props = defineProps({
    placeholder: {
        type: String,
        default: "Search products, categories, sellers...",
    },
    minQueryLength: {
        type: Number,
        default: 2,
    },
    debounceMs: {
        type: Number,
        default: 250,
    },
    searchApiUrl: {
        type: String,
        default: "/api/search",
    },
});

// Localizations
const searchingText = ref("Searching...");
const noResultsText = ref("No results found");
const categoriesText = ref("Categories");
const productsText = ref("Products");
const isVissible = ref(false);
const sellersText = ref("Sellers");
const seeAllText = ref("See all");
const searchAllText = ref("Search all results for");

const toggleSearch = () => {
    isVissible.value = !isVissible.value;
    const localeSwitcher = document.querySelector(".locale-switcher");
    const currencySwitcher = document.querySelector(".currency-switcher");
    
    if (!isVissible.value) {
        searchQuery.value = "";
        
        results.value = {
            categories: { total: 0, hits: [] },
            products: { total: 0, hits: [] },
            sellers: { total: 0, hits: [] },
        };
        
        isOpen.value = false;

        if (localeSwitcher) {
            localeSwitcher.classList.remove("hidden");
        }
        
        if (currencySwitcher) {
            currencySwitcher.classList.remove("hidden");
        }

    } else {
        if (localeSwitcher) {
            localeSwitcher.classList.add("hidden");
        }

        if (currencySwitcher) {
            currencySwitcher.classList.add("hidden");
        }

        setTimeout(() => {
            searchInput.value?.focus();
        }, 50);
    }
};
// Refs
const searchInput = ref(null);
const dropdown = ref(null);

// State
const searchQuery = ref("");
const results = ref({
    categories: { total: 0, hits: [] },
    products: { total: 0, hits: [] },
    sellers: { total: 0, hits: [] },
});
const isOpen = ref(false);
const isLoading = ref(false);
const highlightedIndex = ref(-1);

let debounceTimer = null;

const allResults = computed(() => {
    const items = [];
    // Order: products, categories, sellers
    if (results.value.products?.hits.length) {
        items.push(
            ...results.value.products.hits
                .slice(0, 3)
                .map((item) => ({ ...item, type: "products" })),
        );
    }
    if (results.value.categories?.hits.length) {
        items.push(
            ...results.value.categories.hits
                .slice(0, 3)
                .map((item) => ({ ...item, type: "categories" })),
        );
    }
    if (results.value.sellers?.hits.length) {
        items.push(
            ...results.value.sellers.hits
                .slice(0, 3)
                .map((item) => ({ ...item, type: "sellers" })),
        );
    }
    return items;
});

const hasResults = computed(() => {
    return (
        results.value.categories?.total > 0 ||
        results.value.products?.total > 0 ||
        results.value.sellers?.total > 0
    );
});

// Methods
const onSearchInput = () => {
    clearTimeout(debounceTimer);

    if (searchQuery.value.length < props.minQueryLength) {
        results.value = {
            categories: { total: 0, hits: [] },
            products: { total: 0, hits: [] },
            sellers: { total: 0, hits: [] },
        };
        isOpen.value = false;
        return;
    }

    debounceTimer = setTimeout(() => {
        performSearch();
    }, props.debounceMs);
};

const performSearch = async () => {
    if (searchQuery.value.length < props.minQueryLength) {
        return;
    }

    isLoading.value = true;
    isOpen.value = true;
    highlightedIndex.value = -1;

    try {
        // Enforce maximum 3 items per entity - no custom limit parameter
        const response = await fetch(
            `${props.searchApiUrl}?q=${encodeURIComponent(searchQuery.value)}&entity=all`,
        );

        if (!response.ok) {
            throw new Error("Search request failed");
        }

        const data = await response.json();

        results.value = {
            categories: data.results.categories || { total: 0, hits: [] },
            products: data.results.products || { total: 0, hits: [] },
            sellers: data.results.sellers || { total: 0, hits: [] },
        };
    } catch (error) {
        console.error("Search error:", error);
        results.value = {
            categories: { total: 0, hits: [] },
            products: { total: 0, hits: [] },
            sellers: { total: 0, hits: [] },
        };
    } finally {
        isLoading.value = false;
    }
};

const onFocus = () => {
    if (
        searchQuery.value.length >= props.minQueryLength &&
        (hasResults.value || isLoading.value)
    ) {
        isOpen.value = true;
    }
};

const onBlur = () => {
    setTimeout(() => {
        isOpen.value = false;
    }, 200);
};

const closeDropdown = () => {
    isOpen.value = false;
    searchInput.value?.blur();
};

const getGlobalIndex = (type, item) => {
    let index = 0;
    // Order: products, categories, sellers
    if (results.value.products?.hits.length) {
        if (type === "products") {
            return results.value.products.hits.slice(0, 3).indexOf(item);
        }
        index += Math.min(3, results.value.products.hits.length);
    }
    if (results.value.categories?.hits.length) {
        if (type === "categories") {
            return (
                index + results.value.categories.hits.slice(0, 3).indexOf(item)
            );
        }
        index += Math.min(3, results.value.categories.hits.length);
    }
    if (results.value.sellers?.hits.length) {
        if (type === "sellers") {
            return index + results.value.sellers.hits.slice(0, 3).indexOf(item);
        }
    }
    return -1;
};

const highlightNext = () => {
    if (allResults.value.length === 0) return;
    highlightedIndex.value = Math.min(
        highlightedIndex.value + 1,
        allResults.value.length - 1,
    );
};

const highlightPrev = () => {
    highlightedIndex.value = Math.max(highlightedIndex.value - 1, -1);
};

const onEnter = () => {
    if (
        highlightedIndex.value >= 0 &&
        allResults.value[highlightedIndex.value]
    ) {
        const item = allResults.value[highlightedIndex.value];
        if (item.url) {
            window.location.href = item.url;
        }
    } else {
        window.location.href = getProductSearchUrl();
    }
};

const getCategorySearchUrl = () => {
    return `/search?query=${encodeURIComponent(searchQuery.value)}&category=1`;
};

const formatLabels = {
    webinar: "Webinar",
    "online course": "Online course",
    "onsite course": "On site course",
    "live stream": "Live stream",
};
const formatLabel = (value) => formatLabels[value] || value;

const formatIcons = {
    webinar: WebinarIcon,
    "online course": CourseIcon,
    "onsite course": OnSiteIcon,
    "live stream": VideoIcon,
};
const getFormatIcon = (value) => formatIcons[value] || CourseIcon;

const getProductUrl = (product) => {
    if (product.url_key) {
        return `/${product.url_key}`;
    }
    return `/search?query=${encodeURIComponent(searchQuery.value)}`;
};

const getSellerUrl = (item) => {
    if (item.url) {
        return `/marketplace/${item.url}`;
    }
    return `/search?query=${encodeURIComponent(searchQuery.value)}`;
};

const getProductSearchUrl = () => {
    return `/search?query=${encodeURIComponent(searchQuery.value)}&sort=relevance-asc`;
};

const getSellerSearchUrl = () => {
    return `/search?query=${encodeURIComponent(searchQuery.value)}&seller=1`;
};

const formatPrice = (price) => {
    const currency = window.app?.currency || "USD";
    const locale = window.app?.locale || "en-US";

    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency,
    }).format(price);
};

const handleClickOutside = (event) => {
    if (
        dropdown.value &&
        !dropdown.value.contains(event.target) &&
        !searchInput.value.contains(event.target)
    ) {
        isOpen.value = false;
    }
};

onMounted(() => {
    document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener("click", handleClickOutside);
    clearTimeout(debounceTimer);
});

defineExpose({
    focus: () => searchInput.value?.focus(),
    clear: () => {
        searchQuery.value = "";
        results.value = {
            categories: { total: 0, hits: [] },
            products: { total: 0, hits: [] },
            sellers: { total: 0, hits: [] },
        };
    },
});
</script>
